/**
 * Gráfico Donut (rosca) para unidades com CNPJ
 * Similar ao DonutFarolChart, mas específico para KPIs de unidades com CNPJ no Complement
 */
import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { cn } from '@/lib/utils';
import { Building2 } from 'lucide-react';

interface DonutCnpjData {
  regular: number;
  em_andamento: number;
  irregular: number;
  totalUnidades?: number;
  totalDocumentos?: number;
}

interface DonutCnpjChartProps {
  title: string;
  data: DonutCnpjData | undefined;
  isLoading?: boolean;
  className?: string;
  onSliceClick?: (category: 'regular' | 'em_andamento' | 'irregular') => void;
}

const COLORS = {
  regular: '#10b981', // emerald-500
  em_andamento: '#f59e0b', // amber-500
  irregular: '#ef4444', // red-500
};

const LABELS = {
  regular: 'Regular',
  em_andamento: 'Em Andamento',
  irregular: 'Irregular',
};

export function DonutCnpjChart({
  title,
  data,
  isLoading = false,
  className,
  onSliceClick,
}: DonutCnpjChartProps) {
  const total = (data?.regular || 0) + (data?.em_andamento || 0) + (data?.irregular || 0);
  const safeData = {
    regular: data?.regular || 0,
    em_andamento: data?.em_andamento || 0,
    irregular: data?.irregular || 0,
  };

  const option: EChartsOption = useMemo(() => ({
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const percent = total > 0 ? ((params.value / total) * 100).toFixed(1) : '0';
        return `${params.name}: ${params.value.toLocaleString('pt-BR')} (${percent}%)`;
      },
    },
    legend: {
      show: false,
    },
    series: [
      {
        name: title,
        type: 'pie',
        radius: ['50%', '75%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 4,
          borderColor: 'transparent',
          borderWidth: 2,
        },
        label: {
          show: false,
        },
        emphasis: {
          scale: true,
          scaleSize: 8,
          label: {
            show: true,
            fontSize: 14,
            fontWeight: 'bold',
            formatter: (params: any) => {
              const percent = total > 0 ? ((params.value / total) * 100).toFixed(0) : '0';
              return `${percent}%`;
            },
          },
        },
        data: total > 0 ? [
          { 
            value: safeData.regular, 
            name: LABELS.regular, 
            itemStyle: { color: COLORS.regular } 
          },
          { 
            value: safeData.em_andamento, 
            name: LABELS.em_andamento, 
            itemStyle: { color: COLORS.em_andamento } 
          },
          { 
            value: safeData.irregular, 
            name: LABELS.irregular, 
            itemStyle: { color: COLORS.irregular } 
          },
        ].filter(item => item.value > 0) : [
          { value: 1, name: 'Sem dados', itemStyle: { color: '#e5e7eb' } }
        ],
      },
    ],
    graphic: [
      {
        type: 'text',
        left: 'center',
        top: '42%',
        style: {
          text: total.toLocaleString('pt-BR'),
          fontSize: 24,
          fontWeight: 'bold',
          fill: '#888',
        },
      },
      {
        type: 'text',
        left: 'center',
        top: '55%',
        style: {
          text: 'Total',
          fontSize: 12,
          fill: '#888',
        },
      },
    ],
  }), [title, total, safeData]);

  const handleChartClick = (params: any) => {
    if (!onSliceClick) return;
    
    const nameToCategory: Record<string, 'regular' | 'em_andamento' | 'irregular'> = {
      [LABELS.regular]: 'regular',
      [LABELS.em_andamento]: 'em_andamento',
      [LABELS.irregular]: 'irregular',
    };
    
    const category = nameToCategory[params.name];
    if (category) {
      onSliceClick(category);
    }
  };

  if (isLoading) {
    return (
      <div className={cn(
        'bg-card rounded-lg border p-4 animate-pulse',
        className
      )}>
        <div className="h-4 bg-muted rounded w-32 mb-4" />
        <div className="aspect-square max-w-[200px] mx-auto bg-muted rounded-full" />
      </div>
    );
  }

  return (
    <div className={cn(
      'bg-card rounded-lg border p-4 flex flex-col',
      className
    )}>
      <div className="flex items-center gap-2 mb-2">
        <Building2 className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-medium text-muted-foreground">
          {title}
        </h3>
      </div>
      
      <div className="flex-1 min-h-0">
        <ReactECharts
          option={option}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'canvas' }}
          onEvents={{
            click: handleChartClick,
          }}
        />
      </div>

      {/* Legenda customizada */}
      <div className="flex justify-center gap-4 mt-2">
        {[
          { key: 'regular', label: 'Regular', color: COLORS.regular, value: safeData.regular },
          { key: 'em_andamento', label: 'Andamento', color: COLORS.em_andamento, value: safeData.em_andamento },
          { key: 'irregular', label: 'Irregular', color: COLORS.irregular, value: safeData.irregular },
        ].map((item) => (
          <button
            key={item.key}
            onClick={() => onSliceClick?.(item.key as any)}
            disabled={item.value === 0}
            className={cn(
              'flex items-center gap-1 text-xs transition-all',
              item.value > 0 ? 'hover:opacity-80 cursor-pointer' : 'opacity-50 cursor-not-allowed'
            )}
            title={`${item.value.toLocaleString('pt-BR')} unidades`}
          >
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-muted-foreground">
              {item.label} ({item.value.toLocaleString('pt-BR')})
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
